import React from 'react';
import { useParams } from 'react-router-dom';

import ButtonGoUp from '@components/common/ButtonGoUp/ButtonGoUp';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { ITemplate } from '@model/templateItem';

import { useModalActions } from '@store/useModalStore';

import { useDeleteEquipmentTemplateMutation } from '../../services/useDeleteEquipmentTemplateMutation';
import { useSaveEquipmentTemplateMutation } from '../../services/useSaveEquipmentTemplateMutation';
import {
  selectManageTemplateFilterSelectedRows,
  selectManageTemplateFilterShowIncludedTemplateValue,
  useManageTemplateFilterActions,
} from '../../store/useManageTemplateFilterStore';
import style from './style.module.scss';

interface IProps {
  template?: ITemplate;
  equipmentTableContainerRef: React.LegacyRef<HTMLDivElement>;
}

const Footer: React.FC<IProps> = ({ template, equipmentTableContainerRef }) => {
  const { templateId } = useParams();

  const { t } = useTranslation();

  const { setSecondaryModal } = useModalActions();
  const { setManageTemplateFilterShowIncludedTemplateValue } = useManageTemplateFilterActions();
  const { setManageTemplateFilterSelectedRows } = useManageTemplateFilterActions();

  const { mutate: saveEquipmentTemplateMutate } = useSaveEquipmentTemplateMutation();
  const { mutate: deleteEquipmentTemplateMutate } = useDeleteEquipmentTemplateMutation();

  const selectedRows = selectManageTemplateFilterSelectedRows();
  const showIncludedTemplateValue = selectManageTemplateFilterShowIncludedTemplateValue();

  const handleSuccessMutation = () => {
    showToast({ variant: 'success', message: t('Your changes have been saved.') });

    setManageTemplateFilterSelectedRows(new Map());
  };

  const saveOrDeleteEquipment = (recalculate: number) => {
    const selectedRowsIds: number[] = Array.from(selectedRows.keys());

    if (showIncludedTemplateValue) {
      deleteEquipmentTemplateMutate(
        { id: Number(templateId), equipment: selectedRowsIds, recalculate_cost_saving: recalculate },
        {
          onSuccess: () => {
            handleSuccessMutation();
          },
        },
      );
    } else {
      const includedIds = new Set(template?.settings.map((setting) => setting.equipmentId));
      selectedRowsIds.forEach((id) => includedIds.add(id));
      const selectedEquipments = Array.from(includedIds);

      saveEquipmentTemplateMutate(
        { id: Number(templateId), equipment: selectedEquipments, recalculate_cost_saving: recalculate },
        {
          onSuccess: () => {
            handleSuccessMutation();
            setManageTemplateFilterShowIncludedTemplateValue(true);
          },
        },
      );
    }
  };

  const handleSaveOrDeleteEquipment = () => {
    setSecondaryModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Recalculate archived action items?'),
        bodyContent: t(
          'After saving a new multiplication factor value, do existing completion reports (open and archive) need to be recalculated?',
        ),
        onConfirm: () => {
          saveOrDeleteEquipment(1);
        },
        onCancel: () => {
          saveOrDeleteEquipment(0);
        },
        confirmText: t('Recalculate'),
        cancelText: t(`Don't Recalculate`, {}, true),
      },
    });
  };

  const handleCancel = () => {
    setManageTemplateFilterSelectedRows(new Map());
  };

  return (
    <div className={style.equipmentTableFooter}>
      <div className={style.equipmentTableFooterRight}>
        {!!selectedRows.size && (
          <div className={style.actionButtonWrapper}>
            <span>
              {selectedRows.size} {t(`item${selectedRows.size > 1 ? 's' : ''} selected`)}
            </span>
            <Button text={t('Cancel')} type="secondary" onClick={handleCancel} />
            <div className={style.removeOrSaveButtonWrapper}>
              <Button
                text={showIncludedTemplateValue ? t('Remove from template') : t('Add to template')}
                type="primary"
                variant="small"
                onClick={handleSaveOrDeleteEquipment}
              />
            </div>
          </div>
        )}
        <ButtonGoUp ref={equipmentTableContainerRef} />
      </div>
    </div>
  );
};

export default Footer;
